import axios from "axios";
import { BE_SERVICE_URL, _GQL_API_KEY } from "../utils/constants";
import { log, envs, getAuthToken, removeAuthToken, removeRefreshToken } from "../utils";
import { HTTP_STATUS, AUTH_STORAGE_KEYS } from "../constants/auth";
import { getAuth0TokenSilently, logoutFromAuth0 } from "./auth0";

const _axios = axios.create({
  baseURL: BE_SERVICE_URL,
});

_axios.interceptors.request.use((config) => {
  window?.$loading?.start?.();
  return config;
});

_axios.interceptors.response.use(
  (response) => {
    window?.$loading?.finish?.();
    return response;
  },
  async (error) => {
    window?.$loading?.finish?.();
    const originalRequest = error?.config;

    // Handle 401 Unauthorized: Attempt silent token renewal, or purge session and redirect
    if (
      error?.response?.status === HTTP_STATUS.UNAUTHORIZED &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        log("401 Unauthorized received: attempting silent token renewal via Auth0...");
        const newAuth0Token = await getAuth0TokenSilently();
        if (newAuth0Token) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${getAuthToken()}`;
          return _axios(originalRequest);
        }
      } catch (renewalError) {
        log("Silent token renewal failed: purging session and redirecting to login", renewalError);
        removeAuthToken();
        removeRefreshToken();
        if (typeof window !== "undefined") {
          localStorage.removeItem(AUTH_STORAGE_KEYS.USER_EMAIL);
        }
        await logoutFromAuth0();
        return Promise.reject(renewalError);
      }
    }

    return Promise.reject(error);
  }
);

const _request = async (opts, authorize) => {
  try {
    const headers = {};
    if (authorize) {
      const token = getAuthToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    return await _axios({
      ...opts,
      headers: {
        ...headers,
        ...(opts?.headers || {}),
      },
      timeout: parseInt(envs("HTTP_REQUEST_TIMEOUT")) || 30000,
    });
  } catch (error) {
    window?.$loading?.error?.();
    log(`Request failed w/ error: ${error}`);

    if (error?.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      removeAuthToken();
      removeRefreshToken();
      if (typeof window !== "undefined") {
        localStorage.removeItem(AUTH_STORAGE_KEYS.USER_EMAIL);
      }
      return error.response || null;
    }

    if (error?.code === "ERR_BAD_REQUEST") {
      showErrorMessages(error.response?.data);
      return error.response || null;
    } else {
      window?.$message?.error?.("Request failed");
    }
  }
  return null;
};

export const gqlRequest = async (schema, signal = null, baseURL = null) => {
  const opts = {
    url: "/graphql",
    method: "POST",
    data: schema,
    signal,
    headers: {
      "X-Api-Key": _GQL_API_KEY || envs("GQL_API_KEY"),
    },
  };
  if (baseURL) opts.baseURL = baseURL;
  const response = await _request(opts, true);
  if (response?.data?.errors) {
    showErrorMessages(response.data.errors[0]);
    return null;
  }
  return response?.data;
};

export const apiRequest = async (
  endpoint,
  method,
  data = {},
  signal = null,
  authorize = true
) => {
  const opts = {
    url: endpoint,
    method,
    data,
    signal,
  };
  return await _request(opts, authorize);
};

export const showErrorMessages = (data) => {
  if (data?.message || data?.extensions) {
    const errorMessage = data?.message;
    const errorExtensions =
      data?.extensions?.response?.message ||
      data?.extensions?.exception?.response?.message ||
      null;
    // Note: for blue, show the generic error message
    if (typeof errorMessage === "string" || errorMessage instanceof String)
      window?.$message?.error?.(errorMessage);
    else if (errorExtensions instanceof Array && errorExtensions?.length)
      window?.$message?.error?.(errorExtensions[0]);
    else if (errorMessage instanceof Array && errorMessage?.length)
      window?.$message?.error?.(errorMessage[0]);

    // Note:
    // error message format might be different for GQL queries/mutations
    // might be better to improve this function in the future
  }
};

export const isSuccess = (status) => [HTTP_STATUS.OK, HTTP_STATUS.CREATED].includes(status);

